import { type FormEventHandler, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormHelperText from '@mui/material/FormHelperText'
import TextField, { type TextFieldProps } from '@mui/material/TextField'

interface LoginFormProps {
    onSubmit: FormEventHandler<HTMLFormElement>
    usernameProps: TextFieldProps
    passwordProps: TextFieldProps
    submitDisabled?: boolean
    error?: string
    /** Additional fields rendered between credentials and the submit button. */
    children?: ReactNode
    /** Replaces the credential fields for another sign-in step. */
    fields?: ReactNode
}

const inputProps = {
    variant: 'outlined',
    margin: 'normal',
    required: true,
    fullWidth: true,
    // Validation messages fit in the input margins without shifting the controls.
    sx: { '& .MuiFormHelperText-root': { height: 0, mt: 0, lineHeight: '16px' } },
} as const satisfies TextFieldProps

/** Shared sign-in form presentation; callers own credentials, validation and submission. */
export const LoginForm = ({
    onSubmit,
    usernameProps,
    passwordProps,
    submitDisabled,
    error,
    children,
    fields,
}: LoginFormProps) => (
    <Box component="form" noValidate onSubmit={onSubmit} sx={{ width: '100%', mt: 1 }}>
        {fields ?? (
            <>
                <TextField
                    {...inputProps}
                    {...usernameProps}
                    id="username"
                    label="Username"
                    autoComplete="username"
                    autoFocus
                />
                <TextField
                    {...inputProps}
                    {...passwordProps}
                    id="password"
                    label="Password"
                    type="password"
                    autoComplete="current-password"
                />
            </>
        )}
        {children}
        <Box sx={{ position: 'relative' }}>
            {error && (
                <FormHelperText
                    error
                    variant="filled"
                    sx={{ position: 'absolute', top: -8, height: 'auto', m: 0, mx: '14px', lineHeight: '16px' }}
                >
                    {error}
                </FormHelperText>
            )}
            <Button
                id="login-button"
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 2 }}
                disabled={submitDisabled}
            >
                Sign In
            </Button>
        </Box>
    </Box>
)
